<template>
  <div class="content" :style="{ height: maxHeight }">
    <div class="clear">
      <a-button v-if="!noChange()" type="link" @click="clearForm">
        {{ $t('courseGenerationConfig.clear') }}
      </a-button>
    </div>
    <a-form :form="form" layout="vertical">
      <a-form-item :label="$t('courseGenerationConfig.lessonTheme')">
        <a-input
          v-decorator="['lesson_name', { rules: [{ required: true, message: $t('courseGenerationConfig.lessonThemeRequired') }] }]"
          :placeholder="$t('courseGenerationConfig.lessonThemePlaceholder')"
        />
      </a-form-item>
      <a-form-item :label="$t('courseGenerationConfig.studyPhase')">
        <a-select
          v-decorator="['section_learning', { rules: [{ required: true, message: $t('courseGenerationConfig.studyPhaseRequired') }] }]"
          :placeholder="$t('courseGenerationConfig.studyPhasePlaceholder')"
          allowClear
          :getPopupContainer="getPopupContainer"
        >
          <a-select-option v-for="item in phaseOfStudying" :key="item.id" :value="item.name">
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <template v-if="type === 'course'">
        <a-form-item :label="$t('courseGenerationConfig.chapterCount')">
          <a-input-number
            v-decorator="['chapter_number', { rules: [{ required: true, message: $t('courseGenerationConfig.chapterCountRequired') }] }]"
            style="width: 100%;"
            :min="1"
            :max="10"
            :placeholder="$t('courseGenerationConfig.chapterCountPlaceholder')"
          />
        </a-form-item>
        <a-form-item :label="$t('courseGenerationConfig.sectionCount')">
          <a-input-number
            v-decorator="['section_number', { rules: [{ required: true, message: $t('courseGenerationConfig.sectionCountRequired') }] }]"
            style="width: 100%;"
            :min="1"
            :max="8"
            :placeholder="$t('courseGenerationConfig.sectionCountPlaceholder')"
          />
        </a-form-item>
        <a-form-item :label="$t('courseGenerationConfig.chapterWordsCount')">
          <a-input-number
            v-decorator="[
              'chapter_words_number',
              { rules: [{ required: true, message: $t('courseGenerationConfig.chapterWordsCountRequired') }] }
            ]"
            style="width: 100%;"
            :min="1"
            :max="20"
            :placeholder="$t('courseGenerationConfig.chapterWordsCountPlaceholder')"
          />
        </a-form-item>
      </template>
      <template v-else>
        <a-form-item :label="$t('courseGenerationConfig.teachingGoal')">
          <a-textarea
            v-decorator="['teaching_goal', { rules: [{ required: true, message: $t('courseGenerationConfig.teachingGoalRequired') }] }]"
            style="width: 100%;"
            :placeholder="$t('courseGenerationConfig.teachingGoalPlaceholder')"
          />
        </a-form-item>
        <a-form-item :label="$t('courseGenerationConfig.capabilityGoal')">
          <a-textarea
            v-decorator="['capability_goal', { rules: [{ required: true, message: $t('courseGenerationConfig.capabilityGoalRequired') }] }]"
            style="width: 100%;"
            :placeholder="$t('courseGenerationConfig.capabilityGoalPlaceholder')"
          />
        </a-form-item>
        <a-form-item :label="$t('courseGenerationConfig.teachingDifficulties')">
          <a-textarea
            v-decorator="['teaching_difficulties', { rules: [{ required: true, message: $t('courseGenerationConfig.teachingDifficultiesRequired') }] }]"
            style="width: 100%;"
            :placeholder="$t('courseGenerationConfig.teachingDifficultiesPlaceholder')"
          />
        </a-form-item>
        <a-form-item :label="$t('courseGenerationConfig.qualityGoal')">
          <a-textarea
            v-decorator="['quality_goal', { rules: [{ required: true, message: $t('courseGenerationConfig.qualityGoalRequired') }] }]"
            style="width: 100%;"
            :placeholder="$t('courseGenerationConfig.qualityGoalPlaceholder')"
          />
        </a-form-item>
      </template>
      <a-form-item :label="$t('courseGenerationConfig.uploadMaterials')">
        <a-button class="uploadBtn" @click="handleFileSelect" :disabled="fileList.length > 0">
          <span style="font-size:20px; margin-right: 5px;">+</span>
          <span style="font-size: 12px; padding-right: 5px; margin-top: 2px">{{ $t('courseGenerationConfig.supportedFormats') }}</span> 
          <span style="font-size: 12px;">.docx .pdf .txt .xls .xlsx</span>
        </a-button>
        <input
          type="file"
          ref="fileInput"
          style="display: none"
          @change="handleFileChange"
          accept=".docx,.pdf,.txt,.xls,.xlsx"
        />
        <div class="file-item-wrapper" v-for="(item, index) in fileList" :key="item.uid">
          <a-button type="dashed" style="width: 100%;" class="file-btn">
            <img :src="fileIconMap[item.type]" alt="" srcset="" />
            <a-tooltip>
              <template slot="title">
                {{ item.name }}
              </template>
              <div class="text">{{ item.name }}</div>
            </a-tooltip>
          </a-button>
          <a-progress
            class="progress"
            strokeColor="#ff6d00"
            :strokeWidth="5"
            :percent="item.progress"
            v-if="item.progress > 0 && item.progress < 100"
          />
          <div class="delete-icon" @click="removeFile(index)">
            <img src="@/assets/icons/icon_sc.png" style="width: 15px; cursor: pointer" />
          </div>
        </div>
        <!-- fileList -->
      </a-form-item>
      <a-form-item :label="$t('courseGenerationConfig.associateKnowledgeBase')">
        <div @click="handleSelect">
          <a-input v-model="selectKnowledgeObj.name" :placeholder="$t('courseGenerationConfig.associateKnowledgeBasePlaceholder')">
            <a-icon v-if="!selectKnowledgeObj.name" slot="suffix" type="down" style="font-size: 12px; color: #ccc;" />
            <a-icon v-else @click.stop="selectKnowledgeObj = {}" slot="suffix" type="close-circle" class="close" />
          </a-input>
        </div>
      </a-form-item>

      <a-form-item>
        <a-button type="primary" style="width: 100%;" @click="generate">
          {{ $t('courseGenerationConfig.startGenerate') }}
        </a-button>
      </a-form-item>
    </a-form>
    <!-- 知识库弹窗 -->
    <ZskModal ref="zskModal" @select="selectKnowledge" />
  </div>
</template>

<script>
import { uploadFile } from '@/api/aiApp.js'
import ZskModal from './ZskModal.vue'
import { getKnowledgeBaseList } from '@/api/ai'
import { course } from '@/data/app.js'
const defaultAppId = course.id

export default {
  name: 'CourseGenerationConfig',
  components: {
    ZskModal
  },
  props: {
    outputCompleted: {
      type: Boolean,
      default: false
    },
    maxHeight: {
      type: String,
      default: 'calc(100vh - 217px)'
    },
    type: {
      type: String,
      default: 'course'
    }
  },
  data() {
    return {
      form: this.$form.createForm(this),
      knowledgeList: [],
      fileList: [],
      selectKnowledgeObj: {},
      phaseOfStudying: [
        {
          id: 1,
          name: '小学'
        },
        {
          id: 2,
          name: '初中'
        },
        {
          id: 3,
          name: '高中'
        }
      ],
      fileIconMap: {
        docx: require('@/assets/icons/icon_word.png'),
        pdf: require('@/assets/icons/icon_pdf.png'),
        txt: require('@/assets/icons/icon_text.png'),
        xls: require('@/assets/icons/icon_xlsx.png'),
        xlsx: require('@/assets/icons/icon_xlsx.png'),
        ppt: require('@/assets/icons/icon_ppt.png')
      }
    }
  },
  mounted() {
    if (this.$route.query.conversationId) {
      getKnowledgeBaseList({
        page: 1,
        pageSize: 100
      }).then(res => {
        if (res.code === 200) {
          this.knowledgeList = res.data.list
        }
      })
    }
  },
  methods: {
    noChange() {
      // 检查所有字段都空
      return (
        Object.values(this.form.getFieldsValue()).every(val => val == '' || val == null || val == undefined) &&
        !this.selectKnowledgeObj.name &&
        this.fileList.length == 0
      )
    },
    getPopupContainer(trigger) {
      return trigger.parentElement
    },
    initData(data, messageFiles) {
      this.form.setFieldsValue(data)
      if (data.upload_images === 'T') {
        this.fileList = messageFiles.map(file => {
          // 从文件名中提取后缀
          const fileExtension = file.filename
            .split('.')
            .pop()
            .toLowerCase()

          return {
            name: file.filename,
            type: fileExtension, // 使用提取的后缀作为类型
            progress: 100,
            uid: file.id
          }
        })
      }
      if (data.knowledge_id) {
        console.log(this.knowledgeList)
        this.knowledgeList.forEach(item => {
          if (item.id == data.knowledge_id) {
            this.selectKnowledgeObj = item
          }
        })
      }
    },
    clearForm() {
      // 清空表单
      this.form.resetFields()
      this.selectKnowledgeObj = {}
      // 清空文件列表
      this.fileList = []
      this.$emit('clear')
    },
    handleFileSelect() {
      this.$refs.fileInput.click()
    },
    async handleFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        try {
          const formData = new FormData()
          formData.append('file', file)
          // 从文件名中提取后缀
          const fileExtension = file.name
            .split('.')
            .pop()
            .toLowerCase()

          this.fileList = [
            {
              name: file.name,
              type: fileExtension, // 直接使用提取的后缀
              progress: 0,
              uid: ''
            }
          ]
          const response = await uploadFile(
            { file },
            {
              'X-App-Id': defaultAppId
            },
            p => {
              const realPercent = Math.round((p.loaded * 100) / p.total)
              this.fileList[0].progress = realPercent
            }
          )
          if (response.id) {
            this.fileList[0].uid = response.id
          }
          console.log('文件上传成功:', response)
          // 处理上传成功后的逻辑
        } catch (error) {
          console.error('文件上传失败:', error)
          // 处理上传失败的情况
        }
      }
    },
    selectKnowledge(item) {
      console.log('item', item)
      this.selectKnowledgeObj = item
    },
    generate() {
      this.form.validateFields((err, values) => {
        if (!err) {
          this.newMessage = '' // 清空输入框
          // 准备请求参数
          const params = {
            inputs: Object.assign(
              {
                lesson_name: values.lesson_name,
                section_learning: String(values.section_learning),
                knowledge_id: this.selectKnowledgeObj.id || '',
                upload_images: this.fileList.length > 0 ? 'T' : 'F',
                is_searxng: 'F'
              },
              this.type === 'course'
                ? {
                    chapter_number: String(values.chapter_number),
                    chapter_words_number: String(values.chapter_words_number),
                    section_number: String(values.section_number)
                  }
                : {
                    teaching_goal: values.teaching_goal,
                    capability_goal: values.capability_goal,
                    teaching_difficulties: values.teaching_difficulties,
                    quality_goal: values.quality_goal
                  }
            ),
            files: []
          }
          this.fileList.forEach(item => {
            console.log('FILE item', item)
            params.files.push({
              transfer_method: 'local_file',
              type: 'document',
              upload_file_id: item.uid
            })
          })
          this.$emit('ok', params)
        }
      })
    },
    handleSelect() {
      this.$refs.zskModal.showModal()
      this.$refs.zskModal.selectedRowKeys = [this.selectKnowledgeObj.id]
    },
    removeFile(index) {
      this.fileList.splice(index, 1)
    }
  }
}
</script>

<style lang="less" scoped>
.content {
  width: 100%;
  padding: 0 !important;
  padding-right: 5px !important;
  overflow-y: auto;
  /deep/ .uploadBtn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    color: #4073ce;
  }
  /deep/ .uploadBtn[disabled] {
    color: #ccc;
  }
  /deep/ .ant-btn-primary {
    background: linear-gradient(248deg, #f5a623 0%, #ff6d00 100%);
  }
  .file-item-wrapper {
    position: relative;
    margin-top: 5px;
    .progress {
      position: absolute;
      left: 0;
      bottom: -20px;
    }
    &:hover .delete-icon {
      display: flex;
    }

    .delete-icon {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      display: none;
      align-items: center;
      justify-content: center;
      width: 32px;
      cursor: pointer;
      transition: all 0.3s;
      color: #5a6575;
    }
  }

  .file-btn {
    margin-top: 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    img {
      width: 12px;
      margin-right: 10px;
    }
    .text {
      white-space: nowrap; /* 不换行 */
      overflow: hidden; /* 超出部分隐藏 */
      text-overflow: ellipsis; /* 超出部分显示省略号 */
      max-width: 200px; /* 设置最大宽度 */
    }
  }

  .clear {
    position: absolute;
    top: 4px;
    right: 5px;
    /deep/ .ant-btn-link {
      color: #ff8d29;
      font-size: 13px;
    }
  }
  /deep/ .ant-form-item {
    margin-bottom: 18px !important;
    .close {
      font-size: 12px;
      color: #999;
      display: none;
    }
    .ant-col:hover .close {
      display: block;
    }
  }
}
</style>
